import {
  Dialog,
  DialogFooter,
  DialogType,
  Icon,
  PrimaryButton,
} from "@fluentui/react";
import { toast } from "components/FluentToast";
import { Formik } from "formik";
import { useMoveFleetToAnotherClient } from "hooks/data/mutations/useMoveFleetToAnotherClient";
import usePhraseActivationStatus from "hooks/usePhraseActivationStatus";
import { delay } from "library/delay";
import React from "react";
import { useTranslation } from "react-i18next";
import { setYupLocale } from "utils/setYupLocale";
import * as yup from "yup";
import { FormSpinner } from "../../components/FormSpinner";
import FleetClientComboBox from "./FleetClientComboBox";

type Props = {
  isOpen: boolean;
  fleetId: string | number | null;
  onDismiss: () => void;
  onSuccess?: (movedFleet?: any) => void;
};

export const MoveFleetToAnotherClientDialog = ({
  isOpen,
  fleetId,
  onDismiss,
  onSuccess,
}: Props) => {
  const { t } = useTranslation();
  const phraseActivated = usePhraseActivationStatus().activated;

  const { mutate: onMoveFleet, isLoading } = useMoveFleetToAnotherClient();

  const onSubmit = (values) => {
    onMoveFleet(
      {
        fleetId,
        newClientId: values.newClientId,
      },
      {
        onSuccess: (res) => {
          toast.success(t("bfm.success"));
          onDismiss();
          onSuccess?.(res?.data);
        },
      }
    );
  };

  return (
    <Dialog
      hidden={!isOpen}
      onDismiss={isLoading ? () => {} : onDismiss}
      minWidth={520}
      maxWidth={520}
      modalProps={{
        isBlocking: phraseActivated ? false : true,
        styles: {
          main: { overflow: "visible" },
          scrollableContent: { overflow: "visible" },
        },
      }}
      dialogContentProps={{
        type: DialogType.normal,
        styles: {
          content: { overflow: "visible" },
          inner: { overflow: "visible" },
          innerContent: { overflow: "visible" },
        },
        title: (
          <div
            style={{
              display: "flex",
              alignItems: "center",
            }}
          >
            <Icon
              iconName="Switch"
              style={{
                fontSize: "25px",
                marginRight: "10px",
              }}
            />
            {t("bfm.moveFleetToAnotherClient.label")}
          </div>
        ),
      }}
    >
      <Formik
        initialValues={{ newClientId: null }}
        enableReinitialize
        validationSchema={() => {
          setYupLocale(t);
          return yup.object().shape({
            newClientId: yup.number().nullable().required(),
          });
        }}
        validateOnChange={false}
        validateOnBlur={false}
        onSubmit={async (values, { setSubmitting }) => {
          onSubmit(values);
          await delay(400);
          setSubmitting(false);
        }}
      >
        {(props) => {
          const { handleSubmit } = props;

          return (
            <form onSubmit={handleSubmit}>
              <FleetClientComboBox
                required
                name={"newClientId"}
                label={"bfm.moveFleetToAnotherClient.selectClient.label"}
              />
              <DialogFooter>
                <PrimaryButton
                  text={t("greco.save")}
                  type="submit"
                  iconProps={{
                    iconName: "Switch",
                  }}
                  disabled={isLoading}
                />
              </DialogFooter>
              {isLoading && <FormSpinner />}
            </form>
          );
        }}
      </Formik>
    </Dialog>
  );
};
